/** Formatiert einen ISO-8601-Zeitstempel (UTC) für die deutsche UI in lokaler Zeit. */
const dateTimeFmt = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Die API liefert Zeitstempel als naive DB-Strings ("YYYY-MM-DD HH:MM:SS", UTC,
 * ohne TZ-Suffix). `new Date(...)` würde das ohne Suffix als Browser-Lokalzeit
 * interpretieren -> hier explizit als UTC parsen.
 */
function parseApiUtc(iso: string): Date {
  if (!iso) return new Date(NaN);
  const hasTz = /[Zz]|[+-]\d\d:\d\d$/.test(iso);
  return new Date(hasTz ? iso : `${iso.replace(' ', 'T')}Z`);
}

/** Europe/Berlin-UTC-Offset (Minuten) zum übergebenen Zeitpunkt, DST-bewusst. */
function berlinOffsetMinutes(utcMs: number): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Berlin',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(utcMs)
      .map((p) => [p.type, p.value]),
  );
  const asIfUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour === '24' ? '0' : parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((asIfUtc - utcMs) / 60000);
}

export function formatDateTime(iso: string): string {
  const d = parseApiUtc(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${dateTimeFmt.format(d)} Uhr`;
}

/** true, wenn die Deadline (ISO-UTC) in der Vergangenheit liegt -> Tipps gesperrt. */
export function isPastDeadline(deadlineIso: string, now: Date = new Date()): boolean {
  const deadline = parseApiUtc(deadlineIso);
  if (Number.isNaN(deadline.getTime())) return false;
  return now.getTime() >= deadline.getTime();
}

/**
 * true, wenn der Anpfiff (ISO-UTC) vorbei ist, aber noch innerhalb eines
 * Zeitfensters von `windowHours` liegt — d.h. das Spiel läuft (vermutlich) noch.
 * Das Fenster verhindert, dass ein altes, nie auf 'finished' synchronisiertes
 * Spiel dauerhaft als "live" gilt (spiegelt das Backend-Fenster in
 * ResultSyncService). Deckt 90' + Pause + Nachspielzeit großzügig ab.
 */
export function isLiveWindow(kickoffIso: string, windowHours = 4, now: Date = new Date()): boolean {
  const kickoff = parseApiUtc(kickoffIso);
  if (Number.isNaN(kickoff.getTime())) return false;
  const since = now.getTime() - kickoff.getTime();
  return since >= 0 && since < windowHours * 60 * 60 * 1000;
}

/** Wandelt ISO-UTC -> Wert für <input type="datetime-local"> (Europe/Berlin, ohne Sek.). */
export function isoToLocalInput(iso: string): string {
  const d = parseApiUtc(iso);
  if (Number.isNaN(d.getTime())) return '';
  const utcMs = d.getTime();
  const shifted = new Date(utcMs + berlinOffsetMinutes(utcMs) * 60000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate(),
  )}T${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`;
}

/** datetime-local (Europe/Berlin) -> ISO-8601 UTC für die API. Leerer/ungültiger Input -> null. */
export function localInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const [, y, mo, da, h, mi] = m;
  const guessUtcMs = Date.UTC(Number(y), Number(mo) - 1, Number(da), Number(h), Number(mi));
  return new Date(guessUtcMs - berlinOffsetMinutes(guessUtcMs) * 60000).toISOString();
}

/** Grobe Restzeit-Anzeige bis zur Deadline, z. B. "noch 3 Std." / "noch 2 Tage". */
export function timeUntil(deadlineIso: string, now: Date = new Date()): string {
  const diffMs = parseApiUtc(deadlineIso).getTime() - now.getTime();
  if (Number.isNaN(diffMs) || diffMs <= 0) return 'abgelaufen';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `noch ${minutes} Min.`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `noch ${hours} Std.`;
  const days = Math.round(hours / 24);
  return `noch ${days} Tage`;
}
